import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { ReactElement } from 'react';

import { Offer } from '../../../../common/models/offer';
import { OfferItem } from '../../../../common/models/offer-item';
import { PriceMatrixFooter } from './components/price-matrix-footer';
import { PriceMatrixHeader } from './components/price-matrix-header';
import { PriceMatrixPosition } from './components/price-matrix-position';
import { PriceMatrixPrice } from './components/price-matrix-price';
import { getPriceCalculation } from '../../utils/get-price-calculation';

interface PriceMatrixProps {
    offers: Offer[];
    currency: string;
}

/**
 * A line of the comparison: either one position across every offer, or the
 * closing line that sums each offer up.
 */
interface MatrixLine {
    index: number;
    position?: OfferItem;
}

export const PriceMatrix = ({ offers, currency }: PriceMatrixProps): ReactElement | null => {
    if (0 === offers.length) {
        return null;
    }

    const priceCalculation = getPriceCalculation(offers);

    /** Which offer is cheapest for this position. */
    const bestPriceOfferId = (positionIndex: number): number => {
        let bestPrice = Infinity;
        let bestOfferId = 0;

        offers.forEach(offer => {
            const price = offer.offerItems[positionIndex]?.price ?? Infinity;

            if (price < bestPrice) {
                bestPrice = price;
                bestOfferId = offer.id;
            }
        });

        return bestOfferId;
    };

    const columns: PaloColumn<MatrixLine>[] = [
        {
            // A matrix, not a list: every further column is one supplier's
            // offer, so the widths follow from how many answered rather than
            // from what a column holds. LST-S17 leaves it its own measure.
            key: 'pos',
            width: '40%',
            wrap: true,
            head: <PriceMatrixHeader name={<FormattedMessage id="app.priceMatrix.suppliers" />} bestOffer={false} />,
            cell: line => line.position && <PriceMatrixPosition position={line.index + 1} offerItem={line.position} />,
        },
        ...offers.map((offer, offerIndex) => ({
            key: `supplier${offerIndex}`,
            width: '20%',
            wrap: true,
            highlighted: offer.id === priceCalculation.recommendationId,
            head: (
                <PriceMatrixHeader
                    name={offer.supplierAccount.name}
                    bestOffer={offer.id === priceCalculation.bestOfferId}
                />
            ),
            cell: (line: MatrixLine) =>
                line.position ? (
                    <PriceMatrixPrice
                        currency={currency}
                        // The recommended column is framed, so neither it nor
                        // the one right behind it draws a divider of its own.
                        showDividers={
                            offer.id !== priceCalculation.recommendationId &&
                            offers[offerIndex - 1]?.id !== priceCalculation.recommendationId
                        }
                        offerItem={offer.offerItems[line.index]}
                        bestPrice={offer.id === bestPriceOfferId(line.index)}
                    />
                ) : (
                    <PriceMatrixFooter
                        recommendation={offer.id === priceCalculation.recommendationId}
                        deliveryCost={offer.deliveryPrice}
                        surcharge={offer.surcharge}
                        total={offer.getTotalWithCosts()}
                        currency={currency}
                        paymentTerm={offer.paymentTerm}
                        termsOfDeliveryContent={offer.termsOfDeliveryContent}
                        supplierBonus={offer.supplierBonus}
                    />
                ),
        })),
    ];

    // Every offer answers the same positions, so the first one sets the lines;
    // the last line closes the comparison with each offer's total.
    const lines: MatrixLine[] = [
        ...offers[0].offerItems.map((position, index) => ({ index, position })),
        { index: offers[0].offerItems.length },
    ];

    return (
        <PaloListView<MatrixLine, number>
            embedded={true}
            columns={columns}
            items={lines}
            getRowId={line => line.index}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
        />
    );
};
